/* assets/lab-product-band.js — the product band (DIRECTION.md §4 "Product band", §6
   interactions 6 and 7 on rows). Defines <lab-product-band>: the head switch and the row
   switches resolve one variant per row from the row's JSON map, write it into the kept
   product-form's input[name="id"], swap the Liquid-formatted price, the availability word,
   the preorder word, its numbered milling and ship-by line and properties, and the notify link (never the picture: the row's picture is chosen from the product's
   media by format, not by variant); a chosen processing on the CSS-only filter strip flips
   every row's switch; the added row's name is underlined for 1.2 s after a cart update.

   Submission belongs to product-form.js. Until the cart sheet element exposes the
   renderContents / getSectionsToRender contract (the lab cart-drawer), the band submits the
   row form itself through the same Cart Ajax endpoint, updates #cart-icon-bubble from the
   bundled section and publishes the same cart-update event, so a row add never throws. */

if (!customElements.get('lab-product-band')) {
  /* constants.js declares PUB_SUB_EVENTS with `const`, so the name lives in the global
     lexical scope and never becomes a property of window: `window.PUB_SUB_EVENTS` is
     always undefined. `typeof` on the bare name is the only safe read from another
     classic script — a bare reference would throw if constants.js were ever dropped. */
  const pubSubEvents = () => (typeof PUB_SUB_EVENTS === 'undefined' ? null : PUB_SUB_EVENTS);

  class LabRowMapError extends Error {
    constructor(message, row) {
      super(message);
      this.name = 'LabRowMapError';
      this.row = row;
    }
  }

  class LabProductBand extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.rows = Array.from(this.querySelectorAll('[data-row]'))
        .map((el) => this.readRow(el))
        .filter((row) => row !== null);

      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('submit', (event) => this.onSubmit(event), { capture: true, signal });
      document.addEventListener('change', (event) => this.onFilter(event), { signal });

      const events = pubSubEvents();
      if (typeof subscribe === 'function' && events) {
        this.unsubscribe = subscribe(events.cartUpdate, (data) => this.onCartUpdate(data));
      }

      // a browser may restore radio state on back navigation: reconcile every row once
      this.rows.forEach((row) => this.update(row));
      this.updateHead();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      clearTimeout(this.addedTimer);
    }

    readRow(el) {
      const script = el.querySelector('script[data-variants]');
      if (!script) return null;
      let variants;
      try {
        variants = JSON.parse(script.textContent);
      } catch (error) {
        console.error(new LabRowMapError(`lab-product-band: unreadable variant map (${error.message})`, el));
        return null;
      }
      if (!Array.isArray(variants)) return null;
      return {
        el,
        variants: variants.filter((v) => v && typeof v.id !== 'undefined' && Array.isArray(v.options)),
      };
    }

    /* the selected value per option index: the band's head switch, then the row's own switches */
    selection(row) {
      const values = {};
      const read = (control) => {
        if (control.type === 'radio' && !control.checked) return;
        values[control.dataset.option] = control.value;
      };
      this.querySelectorAll('[data-scope="band"][data-option]').forEach(read);
      row.el.querySelectorAll('[data-option]:not([data-scope="band"])').forEach(read);
      return values;
    }

    resolve(row) {
      const values = this.selection(row);
      const keys = Object.keys(values);
      return row.variants.find((v) => keys.every((k) => v.options[Number(k) - 1] === values[k])) || null;
    }

    update(row) {
      const { el } = row;
      const variant = this.resolve(row);
      const state = el.querySelector('[data-state]');

      /* one word for both states: a combination the store does not make and a variant that
         is out of stock read the same to a buyer (the band renders that word once). */
      if (!variant) {
        el.classList.add('prow--sold-out');
        if (state) state.textContent = this.dataset.soldOutText || '';
        this.unresolve(row);
        return;
      }
      el.classList.toggle('prow--sold-out', !variant.available);
      if (state) state.textContent = variant.available ? '' : this.dataset.soldOutText || '';
      el.dataset.variantId = String(variant.id);

      const idInput = el.querySelector('input[name="id"]');
      if (idInput) idInput.value = String(variant.id);
      const submit = el.querySelector('button[type="submit"]');
      if (submit) {
        if ('unresolved' in el.dataset) submit.disabled = false;
        /* the add in flight keeps its double-submit guard; its finally() reads soldOut */
        submit.dataset.soldOut = String(!variant.available);
        if (!submit.classList.contains('loading')) {
          if (variant.available) submit.removeAttribute('aria-disabled');
          else submit.setAttribute('aria-disabled', 'true');
        }
      }

      el.querySelectorAll('[data-row-link], [data-notify]').forEach((link) => {
        link.href = variant.url;
      });
      delete el.dataset.unresolved;

      this.renderPrice(el, variant);
      this.renderPreorder(el, variant);
    }

    /* a preorder variant (snippets/lab-preorder.liquid) is added under its own word, with its
       ship-by line and the line properties DC Order Limits reads to mark the order; any
       other variant (or none) sends none of them */
    renderPreorder(el, variant) {
      const preorder = variant?.available ? variant.preorder : null;
      const ship = el.querySelector('[data-ship]');
      if (ship) {
        /* two stacked lines: the numbered milling (only where the band asks for it, and only
           beside a dated sentence) above the Dash ship-by sentence */
        const line = preorder?.line || '';
        const shipText = ship.querySelector('[data-ship-text]');
        const milling = ship.querySelector('[data-milling]');
        if (shipText) shipText.textContent = line;
        if (milling) milling.textContent = line ? preorder?.milling || '' : '';
        ship.hidden = !line;
      }
      const prop = el.querySelector('[data-preorder-prop]');
      if (prop) prop.disabled = !preorder;
      [
        ['[data-preorder-on]', preorder?.on],
        ['[data-preorder-by]', preorder?.by],
        ['[data-preorder-milling]', preorder?.milling_property],
      ].forEach(([selector, value]) => {
        const input = el.querySelector(selector);
        if (!input) return;
        input.value = value || '';
        input.disabled = !value;
      });
      const button = el.querySelector('[data-add-label]');
      const text = button?.querySelector('[data-add-text]');
      if (text) text.textContent = preorder ? button.dataset.preorderLabel : button.dataset.addLabel;
      this.syncShip();
    }

    /* every row shows the same ship-by sentence: say it once in the rows' head line (the CSS
       hides the row copies only where that head line is drawn); any row without it, or with
       another date, keeps every row's own. Only the sentence is lifted: a row's numbered
       milling stays on the row, since N. 1 and N. 2 can differ row by row */
    syncShip() {
      const head = this.querySelector('[data-band-ship]');
      if (!head) return;
      const texts = new Set(
        this.rows.map(({ el }) => {
          const ship = el.querySelector('[data-ship]');
          const text = ship?.querySelector('[data-ship-text]');
          return ship && !ship.hidden && text ? text.textContent : '';
        })
      );
      const shared = this.rows.length > 1 && texts.size === 1 && !texts.has('');
      head.textContent = shared ? [...texts][0] : '';
      this.toggleAttribute('data-ship-shared', shared);
    }

    /* A combination the store does not make fails closed: nothing in the row may still name
       the previous variant, or product-form.js (and the bottom strip's buy button, which
       submits the same form) would add it. The id goes empty, the button is disabled for
       the browser and for assistive tech, and the links open the product, not a variant. */
    unresolve(row) {
      const { el } = row;
      el.dataset.unresolved = '';
      delete el.dataset.variantId;
      const idInput = el.querySelector('input[name="id"]');
      if (idInput) idInput.value = '';
      this.renderPreorder(el, null);
      const submit = el.querySelector('button[type="submit"]');
      if (submit) {
        submit.disabled = true;
        submit.dataset.soldOut = 'true';
        submit.setAttribute('aria-disabled', 'true');
      }
      const productUrl = row.variants[0]?.url.split('?')[0];
      if (productUrl) {
        el.querySelectorAll('[data-row-link], [data-notify]').forEach((link) => {
          link.href = productUrl;
        });
      }
    }

    /* the head line follows the head switch. "Da" is snippets/lab-price-from.liquid's
       floor of the chosen harvest, rendered by Liquid once per harvest into data-floors:
       this only swaps the markup in, it never picks a variant itself. The band's own link
       opens the product on the first row's variant rather than on the store's default. */
    updateHead() {
      const control = this.querySelector('[data-scope="band"][data-option]:checked');
      if (!control) return;
      const from = this.querySelector('[data-from-price]');
      const floors = this.floors();
      if (from && floors && typeof floors[control.value] === 'string') from.innerHTML = floors[control.value];
      const cta = this.querySelector('[data-cta-follow]');
      const first = this.rows[0];
      const variant = first ? this.resolve(first) : null;
      if (cta && variant) cta.href = variant.url;
      else if (cta && first?.variants[0]) cta.href = first.variants[0].url.split('?')[0];
    }

    floors() {
      if (this.floorMap !== undefined) return this.floorMap;
      const script = this.querySelector('script[data-floors]');
      this.floorMap = null;
      if (!script) return null;
      try {
        this.floorMap = JSON.parse(script.textContent);
      } catch (error) {
        console.error(new LabRowMapError(`lab-product-band: unreadable floor map (${error.message})`, script));
      }
      return this.floorMap;
    }

    renderPrice(el, variant) {
      // a sold-out products row states its harvest's floor (lab-price-from), not a variant
      if (el.querySelector('[data-price] .lab-price--floor')) return;
      const price = el.querySelector('[data-price] .price');
      if (!price) return;
      const unit = price.querySelector('.unit-price .price-item');
      if (unit) {
        unit.textContent = variant.unit || '';
        unit.closest('.unit-price').hidden = !variant.unit;
      }
      const regular = price.querySelector('.price__regular .price-item--regular');
      const saleCompare = price.querySelector('.price__sale s.price-item--regular');
      const sale = price.querySelector('.price__sale .price-item--sale');
      const onSale = Boolean(variant.compare);
      if (regular) regular.textContent = variant.price;
      if (sale) sale.textContent = variant.price;
      if (saleCompare) saleCompare.textContent = variant.compare || '';
      price.classList.toggle('price--on-sale', onSale && Boolean(saleCompare));
      price.classList.toggle('price--sold-out', !variant.available);
    }

    onChange(event) {
      const control = event.target;
      if (!(control instanceof HTMLElement) || !control.matches('[data-option]')) return;
      if (control.dataset.scope === 'band') {
        this.rows.forEach((row) => this.update(row));
        this.updateHead();
        return;
      }
      const rowEl = control.closest('[data-row]');
      const row = this.rows.find((r) => r.el === rowEl);
      if (row) this.update(row);
    }

    /* the CSS-only filter strip: a chosen processing also flips every row's switch to it,
       so the price on every row and the add button follow the filter */
    onFilter(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.dataset.shopFilter !== 'processing') return;
      if (!input.checked || input.value === 'all') return;
      this.rows.forEach((row) => {
        const radio = row.el.querySelector(`input[type="radio"][data-option][data-key="${CSS.escape(input.value)}"]`);
        if (!radio || radio.checked) return;
        radio.checked = true;
        this.update(row);
      });
    }

    onCartUpdate(data) {
      if (!data || data.source !== 'product-form' || !data.productVariantId) return;
      this.markAdded(String(data.productVariantId));
    }

    markAdded(variantId) {
      const rows = this.rows.filter((row) => row.el.dataset.variantId === variantId);
      if (!rows.length) return;
      clearTimeout(this.addedTimer);
      rows.forEach((row) => row.el.classList.add('row--added'));
      const status = this.querySelector('[data-status]');
      if (status) {
        const name = rows[0].el.querySelector('.row__name')?.textContent.trim() || '';
        status.textContent = `${this.dataset.addedText || ''} · ${name}`.trim();
      }
      this.addedTimer = setTimeout(() => {
        rows.forEach((row) => row.el.classList.remove('row--added'));
      }, 1200);
    }

    /* ---------- submission fallback (only while no cart element can render the sheet) ---------- */
    cartCanRender() {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      return Boolean(cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function');
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('[data-row]')) return;
      if (this.cartCanRender()) return; // product-form.js owns the submit
      event.preventDefault();
      event.stopPropagation();
      this.submitRow(form);
    }

    submitRow(form) {
      const button = form.querySelector('button[type="submit"]');
      const spinner = form.querySelector('.loading__spinner');
      if (button?.getAttribute('aria-disabled') === 'true') return;

      button?.setAttribute('aria-disabled', 'true');
      button?.classList.add('loading');
      spinner?.classList.remove('hidden');
      this.showError(form, '');

      const body = new FormData(form);
      body.append('sections', 'cart-icon-bubble');
      body.append('sections_url', window.location.pathname);
      const variantId = String(body.get('id') || '');

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = body;

      fetch(window.routes?.cart_add_url || '/cart/add', config)
        .then((response) => response.json())
        .then((response) => {
          const events = pubSubEvents();
          const canPublish = typeof publish === 'function' && Boolean(events);
          if (response.status) {
            this.showError(form, response.description || response.message || window.cartStrings?.error || '');
            if (canPublish) {
              publish(events.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            return;
          }
          this.renderBubble(response.sections?.['cart-icon-bubble']);
          if (canPublish) {
            publish(events.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: response });
          } else {
            this.markAdded(variantId);
          }
        })
        .catch((error) => {
          console.error(error);
          this.showError(form, window.cartStrings?.error || error.message);
        })
        .finally(() => {
          button?.classList.remove('loading');
          if (button?.dataset.soldOut !== 'true') button?.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        });
    }

    renderBubble(html) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble || typeof html !== 'string') return;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (section) bubble.innerHTML = section.innerHTML;
    }

    showError(form, message) {
      const wrapper = form.querySelector('.product-form__error-message-wrapper');
      const text = form.querySelector('.product-form__error-message');
      if (!wrapper || !text) return;
      wrapper.toggleAttribute('hidden', !message);
      text.textContent = message || '';
    }
  }

  customElements.define('lab-product-band', LabProductBand);
}
